import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest, isAdminFromClaims } from '../../authConfig';

/**
 * ProtectedRoute Component
 * 
 * Wraps admin routes to ensure only authenticated Microsoft Entra ID users can access them.
 * 
 * Security-hardened: All environments require Microsoft Entra ID authentication.
 */
const ProtectedRoute = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [isCheckingGroup, setIsCheckingGroup] = useState(true);
  const [isInAdminGroup, setIsInAdminGroup] = useState(false);
  
  useEffect(() => {
    const checkAdminRole = async () => {
      if (accounts.length === 0) {
        setIsCheckingGroup(false);
        return;
      }
      
      try {
        const account = accounts[0];
        const idTokenClaims = account.idTokenClaims;
        
        // Check roles claim from ID token
        // App roles are ALWAYS included in the ID token - no Graph API needed!
        const hasAdminRole = isAdminFromClaims(idTokenClaims);
        
        if (hasAdminRole) {
          console.log('✅ Admin verified from ID token roles claim');
          
          // Store the ID token for API authentication
          // Admin API calls need this token to verify the user
          
          // First, check if we already have a token from handleRedirectPromise
          const existingToken = localStorage.getItem('token');
          if (existingToken) {
            console.log('✅ Token already in localStorage (from redirect handling), length:', existingToken.length);
          } else {
            // Try to get a fresh token via acquireTokenSilent
            try {
              console.log('🔐 Attempting to acquire token silently...');
              const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: account
              });
              
              console.log('🔐 Token response received:', !!tokenResponse);
              console.log('🔐 ID token present:', !!tokenResponse?.idToken);
              
              if (tokenResponse && tokenResponse.idToken) {
                localStorage.setItem('token', tokenResponse.idToken);
                console.log('✅ ID token stored for API calls');
                console.log('✅ Token length:', tokenResponse.idToken.length);
              } else {
                console.error('❌ No ID token in acquireTokenSilent response');
                
                // Fallback: Try to use the ID token from the account's cache
                // The account object may have cached ID token claims but not the raw token
                // This is a last resort
                console.log('🔐 Attempting fallback: checking for cached token in MSAL...');
              }
            } catch (error) {
              console.error('❌ Failed to acquire token silently:', error);
              console.error('❌ Error details:', error.message, error.errorCode);
              
              // If acquireTokenSilent fails, try interactive token acquisition
              // This handles edge cases where the cache is stale
              if (error.name === 'InteractionRequiredAuthError') {
                console.log('🔐 Interactive authentication required, will redirect...');
                try {
                  await instance.acquireTokenRedirect({
                    ...loginRequest,
                    account: account
                  });
                } catch (redirectError) {
                  console.error('❌ acquireTokenRedirect failed:', redirectError);
                }
              }
            }
          }
        } else {
          console.log('❌ User does not have admin role');
        }
        
        setIsInAdminGroup(hasAdminRole);
      } catch (error) {
        console.error('Error checking admin role:', error);
        setIsInAdminGroup(false);
      } finally {
        setIsCheckingGroup(false);
      }
    };
    
    if (accounts.length > 0 && inProgress === 'none') {
      checkAdminRole();
    } else if (inProgress === 'none') {
      setIsCheckingGroup(false);
    }
  }, [accounts, instance, inProgress]);
  
  // Not logged in - redirect to Microsoft login
  if (accounts.length === 0 && inProgress === 'none') {
    console.log('🔐 No account detected - redirecting to Microsoft login');
    instance.loginRedirect(loginRequest);
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Redirecting to Microsoft login...</p>
        </div>
      </div>
    );
  }
  
  // Login in progress
  if (inProgress !== 'none' || isCheckingGroup) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Verifying permissions...</p>
        </div>
      </div>
    );
  }
  
  // Logged in but does not have admin role
  if (!isInAdminGroup) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="max-w-2xl p-8 bg-gray-800 rounded-lg border border-gray-700">
          <h1 className="text-3xl font-bold mb-4 text-red-400">
            🔒 Access Denied
          </h1>
          <p className="text-gray-300 mb-4">
            You are not authorized to access admin features.
          </p>
          <p className="text-gray-400 mb-4">
            Logged in as: <strong>{accounts[0]?.username}</strong>
          </p>
          <p className="text-gray-500 text-sm mt-4">
            Please contact your administrator to be assigned the "Admin" role in the TagQuest application.
          </p>
          <div className="mt-4 bg-blue-500/20 border border-blue-500 rounded p-3 text-sm">
            <p className="text-blue-400 mb-1">For Administrators:</p>
            <p className="text-gray-400">
              Assign the Admin role in Azure AD → Enterprise Applications → TagQuest → Users and groups
            </p>
          </div>
          <button
            onClick={() => instance.logout()}
            className="mt-6 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }
  
  // Authenticated and in admin group - allow access
  console.log('✅ Admin access granted');
  return children;
};

export default ProtectedRoute;
